// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

// CR henry 2026-06-25 (Nav §2.2 — "Vet Portal blank page on first load"):
// the SPA shipped plotly (~4.9 MB) on the login route's static graph because
// every chart component statically imported `react-plotly.js`. That meant
// every cold visitor downloaded the whole plot library before seeing the
// login form. This wrapper breaks the static chain — chart components now
// see plotly via a Suspense boundary, and Vite emits the plot bundle as a
// genuinely lazy chunk (no entry modulepreload tag).
//
// Use as a near-drop-in replacement for `react-plotly.js`:
//   import { LazyPlot as Plot } from '../common/LazyPlot'
//   ...<Plot data={...} layout={...} />
//
// The fallback height defaults to props.layout.height (matches the chart's
// final layout) so chart cards don't reflow when plotly resolves.

import React, { type ComponentProps, Suspense } from 'react'
import { useTranslate } from 'react-admin'

const Plot = React.lazy(() => import('react-plotly.js'))

type PlotProps = ComponentProps<typeof Plot>

function PlotFallback({ height }: { height?: number | string }) {
  const translate = useTranslate()
  return (
    <div
      data-testid="lazy-plot-fallback"
      style={{
        height: height ?? 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#999',
        fontSize: 13,
      }}
    >
      {translate('pos.glucose.loading_chart')}
    </div>
  )
}

export function LazyPlot(props: PlotProps) {
  const height = (props.layout?.height ?? undefined) as number | string | undefined
  return (
    <Suspense fallback={<PlotFallback height={height} />}>
      <Plot {...props} />
    </Suspense>
  )
}
